import {
    useCallback,
    useEffect,
    useState,
} from 'react'
import noop from 'lodash/noop'
import isEqual from 'lodash/isEqual'

import replaceTempIds from 'app/utils/replaceTempIds'
import useHandleStatusRequest from 'app/hooks/useHandleStatusRequest'
import DomainObjectFormWithControls from 'app/shared-components/DomainObjectFormWithControls'
import {
    RequestMethods,
} from 'app/enums/common.enums'
import {
    FormFields,
} from 'app/types/form.types'
import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import useSkyNetConfirmationContext from '../SkyNetConfirmation/useSkyNetConfirmationContext'

type Props = {
    value?: Record<string, any>,
    name: string,
    onSuccess?: (data: Record<string, any>) => void,
    onError?: (err: any) => void,
    setExternalValue?: (data: Record<string, any>) => void,
    requestParams: {
        url: string,
        method: string,
    },
    modifyDataBeforeSend?: (data: Record<string, any>) => Record<string, any>
    onCancel?: () => void
    disabled?: boolean,
    exists?: boolean,
    className?: string,
    canDelete?: boolean,
    canUpdate?: boolean,
    canCancel?: boolean,
    createEnabled?: boolean,
    cancelEnabled?: boolean,
    customLabelSave?: string,
    'data-testid'?: string,
    customButtons?: JSX.Element[],
    getCustomButtons?: (a: any) => JSX.Element[],
    classNames?: {
        gridWrapper?: string,
        gridAreaTemplate?: string,
        gridCardWrapper?: string,
        title?: string,
    },
    fields: FormFields,
    noCard?: boolean,
    wrapper?: string,
}

const defaultProps = {
    value: {},
    onSuccess: noop,
    onError: noop,
    onCancel: noop,
    setExternalValue: noop,
    modifyDataBeforeSend: (value) => {
        return value
    },
    disabled: false,
    exists: false,
    className: undefined,
    canDelete: false,
    canCancel: true,
    canUpdate: true,
    createEnabled: true,
    cancelEnabled: false,
    customLabelSave: undefined,
    'data-testid': undefined,
    customButtons: undefined,
    classNames: undefined,
    noCard: false,
    wrapper: undefined,
    getCustomButtons: undefined,
}

const FormWithControls = ({
    value,
    name,
    disabled,
    exists,
    requestParams,
    onSuccess,
    onError,
    modifyDataBeforeSend,
    setExternalValue,
    onCancel,
    className,
    canDelete,
    canCancel,
    canUpdate,
    createEnabled,
    cancelEnabled,
    customLabelSave,
    'data-testid': dataTestid,
    getCustomButtons,
    customButtons,
    classNames,
    fields,
    noCard,
    wrapper,
}: Props) => {
    const [defaultData] = useState<Record<string, any>>(value)
    const [
        data,
        setData,
    ] = useState<Record<string, any>>(value)

    const {
        setOpen,
        setAction,
    } = useSkyNetConfirmationContext() || {}

    useEffect(() => {
        setData((oldData) => {
            if (value !== oldData) {
                return value
            }
            return oldData
        })
    }, [value])

    const {
        onStartSaving,
        onError: onErrorHandler,
    } = useHandleStatusRequest({
        id: name,
    })

    const onChange = (newValue: Record<string, any>) => {
        setData(newValue)
        setExternalValue(newValue)
    }

    const cancelChanges = () => {
        setData(defaultData)
        onCancel()
    }

    const createSuccessHandler = useCallback(
        (action: RequestMethods) => {
            return (resp: Record<string, any>) => {
                onSuccess({
                    ...resp, action,
                })
            }
        },
        [onSuccess],
    )

    const updateRequest = useSimpleApiRequest({
        name,
        requestParams,
        onSuccess: createSuccessHandler(RequestMethods.UPDATE),
        onError,
    })

    const createRequest = useSimpleApiRequest({
        title: 'The object has been created correctly.',
        name,
        requestParams,
        onSuccess: createSuccessHandler(RequestMethods.CREATE),
        onError,
    })

    const deleteRequest = useSimpleApiRequest({
        name,
        requestParams,
        onSuccess: createSuccessHandler(RequestMethods.DELETE),
        onError,
    })

    const getModifiedData = useCallback((): Promise<Record<string, any>> => {
        const modifiedData = modifyDataBeforeSend(replaceTempIds(data))

        if (!(modifiedData instanceof Promise)) {
            return Promise.resolve(modifiedData)
        }

        return modifiedData
    }, [
        modifyDataBeforeSend,
        data,
    ])

    const executeWithData = useCallback(
        (request) => {
            getModifiedData()
                .then((modifiedData) => {
                    onStartSaving()
                    request.mutate(modifiedData)
                })
                .catch(onErrorHandler)
        },
        [
            getModifiedData,
            onErrorHandler,
            onStartSaving,
        ],
    )

    const updateEntity = useCallback(() => {
        executeWithData(updateRequest)
    }, [
        executeWithData,
        updateRequest,
    ])

    const createEntity = useCallback(() => {
        executeWithData(createRequest)
    }, [
        executeWithData,
        createRequest,
    ])

    const deleteEntity = useCallback(() => {
        onStartSaving()
        deleteRequest.mutate(undefined)
    }, [
        deleteRequest,
        onStartSaving,
    ])

    const onDelete = useCallback(() => {
        if (setAction && setOpen) {
            setAction(() => {
                return deleteEntity
            })
            setOpen(true)
            return
        }

        deleteEntity()
    }, [
        deleteEntity,
        setAction,
        setOpen,
    ])

    return (
        <DomainObjectFormWithControls
            value={data}
            name={name}
            onChange={onChange}
            reset={cancelChanges}
            update={updateEntity}
            create={createEntity}
            remove={onDelete}
            edited={!isEqual(data, defaultData)}
            disabled={disabled}
            exists={exists}
            className={className}
            canDelete={canDelete}
            canCancel={canCancel}
            canUpdate={canUpdate}
            createEnabled={createEnabled}
            cancelEnabled={cancelEnabled}
            customLabelSave={customLabelSave}
            data-testid={dataTestid}
            getCustomButtons={getCustomButtons}
            customButtons={customButtons}
            classNames={classNames}
            fields={fields}
            noCard={noCard}
            wrapper={wrapper}
            isLoading={createRequest?.isPending || updateRequest?.isPending}
        />
    )
}

FormWithControls.defaultProps = defaultProps

export default FormWithControls
